import { DatePipe, TitleCasePipe } from '@angular/common';
import { routerNavigationAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import { SharedActions } from '../../shared/actions';
import { FormatDatePipe } from '../../shared/pipes/FormatDatePipe';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { amountFormatter } from '../../shared/utilities/AmountFormatter';
import { CheckAbbreviatedRecipientTypes, CheckDeliveryCodesType, Frequencies, amountTypeDict, checkRecipientTypeDict, frequencyDict, navRouteConstants, transactionTypes, yesNo } from '../../shared/utilities/Constants';
import { percentFormatter } from '../../shared/utilities/PercentFormatter';
import { ViewPageActions } from '../../transaction-view/actions';
import {
  AchActions,
  CheckActions,
  ConfirmationPageActions,
  InternalTransferActions,
  PtrsActions,
  ReviewPageActions,
  TransactionDetailsActions,
  TransactionFormActions,
  TransactionsTableActions,
  WireActions,
} from '../actions';
import { mapApiResponseToTransInquiryInfo } from '../effects/ptrs.effects';
import { TrustTaxCategoriesAndCodes } from '../models/ach/TrustFieldsResponse';
import {
  AchForm,
  AchTransactionDetails,
  CheckForm,
  ItForm,
  ItTransactionDetails,
  TransactionForm,
  TransactionFormServiceError,
  WireExtraPrompts,
  WireForm,
  isAchForm
} from '../models/form/TransactionForm';
import { getDateWOTimezone } from '../../shared/utilities/datetime-utils';

export const stagedFormKey = 'stagedTransactionForm';

export type State = TransactionForm;

export const initialTransactionFormServiceError: TransactionFormServiceError = {
  status: 0,
  url: '',
  name: '',
  message: '',
  correlationId: '',
  hasError: false
};

export const initialTransactionFormState: State = {
  loading: false,
  error: '',
  hasError: false,
  transactionType: '',
  extraPrompts: {
    useStandingAuth: '',
    requestingAccountHolderId: ''
  },
  transactionDetails: {},
  valid: false,
  showCashieringIRAAgreement: false,
  serviceError: initialTransactionFormServiceError,
  stateList: [],
  countryList: [],
  prefilledFlow: {
    state: '',
    name: ''
  },
  createTransactionInProgress: false,
  continueClickTotalDistTriggered: false,
  acknowledgeCallV3FailedForToggleOn: false,
  acknowledgeCallV3Failed: false
};

export const reducer = createReducer(
  initialTransactionFormState,
  on(
    TransactionFormActions.callWiresVerifyV3,
    TransactionFormActions.callWireSloaVerifyV3FromCreateTransaction,
    TransactionFormActions.callAchVerify,
    TransactionFormActions.callOnetimeCheckVerify,
    TransactionFormActions.callRecurringCheckVerify,
    TransactionFormActions.loadChecksReviewPageWithoutVerifyCall,
    TransactionFormActions.loadInternalTransferWithoutVerifyCall,
    (state, transactionForm) => {
      return {
        ...transactionForm,
      };
    }
  ),
  on(TransactionFormActions.callInternalTransferListVerify, (state, { transactionForm }) => {
    return { ...transactionForm}
  }),
  on(TransactionFormActions.continueClickTotalDist, (state, {transactionForm}) => {
    return {
      ...transactionForm,
      continueClickTotalDistTriggered: true 
    }
  }),
  on(TransactionFormActions.loadReviewPage, (state) => {
    return {
      ...state,
      continueClickTotalDistTriggered: false
    };
  }),
  on(TransactionFormActions.callAchAddProfileVerify, (state, { transactionForm }) => {
    return { ...transactionForm}
  }),
  on(TransactionFormActions.callInternalTransferVerify, (state, { transactionForm }) => {
    return { ...transactionForm}
  }),
  on(
    TransactionFormActions.loadChecksReviewPageWithoutVerifyCall,
    (state, transactionForm) => {
      return {
        ...transactionForm,

      }
    }
  ),
  on(
    TransactionFormActions.editLoadComplete,
    TransactionFormActions.cancelClicked,
    ReviewPageActions.cancelClicked,
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    () => {
      return initialTransactionFormState;
    }
  ),
  on(routerNavigationAction, (state, routeParams) => {
    if (
      routeParams.payload.routerState.url === navRouteConstants.createTransaction
    ) {
      return initialTransactionFormState;
    }
    return { ...state };
  }),
  on(TransactionDetailsActions.loadWithdrawalDateSuccess, (state, value) => {
    if (isAchForm(state)) {
      let finalState = {
        ...state,
        transactionDetails: {
          ...state.transactionDetails,
          withdrawalDate: value.initiationDate,
        }
      } as AchForm;
      if (state.extraPrompts.transferDirection === 'incoming') {
        //Will need both fundsAvailableToTrade to display to UI and settlementDate to send to cashiering(ProcessMoneyLinkSI -1 day)
        finalState.transactionDetails.fundsAvailableToTrade = value.fundsCreditDate;
        finalState.transactionDetails.settlementDate = value.settlementDate;
      }
      return finalState;
    }
    return { ...state };
  }),

  on(WireActions.verifyV3CallSuccessful, (state, response) => {
    return {
      ...state,
      transactionDetails: {
        ...state.transactionDetails,
      }
    };
  }),
  on(WireActions.callWireSloaVerifyV3FromCreateTransactionSuccessful, (state, response) => {
    return {
      ...state,
      transactionDetails: {
        ...state.transactionDetails,
      }
    };
  }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, { internalTransferInquiryResponse }) => {
    let useStandingAuth = internalTransferInquiryResponse?.isLoaOnFile;
    let fedTax: string = '';
    let stateTax: string = '';
    if (internalTransferInquiryResponse?.iraFields?.isIraTransaction){
      fedTax = internalTransferInquiryResponse?.iraFields?.isWithholdFedTax ? percentFormatter(internalTransferInquiryResponse?.iraFields?.fedTaxAmount) : '0%';
      stateTax = internalTransferInquiryResponse?.iraFields?.isWithholdStateTax ? percentFormatter(internalTransferInquiryResponse?.iraFields?.stateTaxAmount) : '0%';
    }
    let amountTypeSelection = getGrossNetSelection(internalTransferInquiryResponse.grossAmountOriginCode, internalTransferInquiryResponse.netAmountOriginCode);

    let response = {
      ...state,
      transactionType: transactionTypes.internalTransfer,
      extraPrompts: {
        ...state.extraPrompts,
        useStandingAuth: useStandingAuth ? yesNo.yes : yesNo.no,
        frequency: getFrequency(internalTransferInquiryResponse.recurringFields?.frequency?.code),
        transferType: 'cash'
      },
      transactionDetails: {
        ...state.transactionDetails,
        toAccount: internalTransferInquiryResponse?.toAccountInfo?.accountNumber,
        processDate: internalTransferInquiryResponse?.processDate,
        amount: amountTypeSelection === 'net' ? internalTransferInquiryResponse.netCashAmount : internalTransferInquiryResponse?.totalWithdrawalAmount,
        amountType: amountTypeSelection,
        iraContributionType: internalTransferInquiryResponse.iraContributionTypeMap ? internalTransferInquiryResponse.iraContributionType.description : '',
        taxWithholdingsFormGroup: {
          ...state.transactionDetails.taxWithholdingsFormGroup!,
          fedTaxPercent: fedTax,
          stateTaxPercent: stateTax,
        },
        recurringFieldsFormGroup: {
          ...(state.transactionDetails as ItTransactionDetails).recurringFieldsFormGroup,
          secondProcessDate: internalTransferInquiryResponse?.recurringFields?.secondProcessDate,
          endDate: internalTransferInquiryResponse?.recurringFields?.endDate,
          recurringFrequency: internalTransferInquiryResponse?.recurringFields?.frequency?.code,
          payIncome: internalTransferInquiryResponse?.recurringFields?.payIncome?.code?.trim() ?? '',
        }
      },
      loading: false
    } as ItForm;

    if (internalTransferInquiryResponse.isBundledTransaction){
      response.extraPrompts = {
        ...state.extraPrompts,
        useStandingAuth: 'bundled',
        useBundledStandingAuth: useStandingAuth
          ? 'yes'
          : getFrequency(
              internalTransferInquiryResponse.recurringFields?.frequency?.code
            ) === Frequencies.onetime
          ? 'no'
          : 'recurring',
        transferType: 'cash',
        frequency: getFrequency(internalTransferInquiryResponse.recurringFields?.frequency?.code) as keyof typeof frequencyDict
      };
    }

    if (internalTransferInquiryResponse.ptrsTransactions.length > 0) {
      let transInquiryTrustFields = mapApiResponseToTransInquiryInfo(internalTransferInquiryResponse.ptrsTransactions);
      const titleCasePipe = new TitleCasePipe();
      response = {
        ...response,
        transactionDetails: {
          ...response.transactionDetails,
          trustTaxCategory: getTrustTaxCategory(internalTransferInquiryResponse?.taxCodeList, transInquiryTrustFields.trustTaxCode),
          trustIncomePercent: transInquiryTrustFields.trustIncomePercent + '%',
          trustPrincipalPercent: transInquiryTrustFields.trustPrincipalPercent + '%',
          trustTaxCode: transInquiryTrustFields.trustTaxCode,
          trustPaidFor: titleCasePipe.transform(transInquiryTrustFields.trustPaidFor),
          trustIncomeToPercent: transInquiryTrustFields.trustIncomeToPercent + '%',
          trustPrincipalToPercent: transInquiryTrustFields.trustPrincipalToPercent + '%',
          trustDepositCode: transInquiryTrustFields.trustDepositCode,
          trustComment: transInquiryTrustFields.trustComment
        }
      }
    }

    return response;
  }),
  on(WireActions.inquiryCallSuccessful, (state, response) => {
    // To check whether wire is three/four step and to determine the recipient bank/first intermediary position
    const isThreeStep = response?.wireInquiryResponse?.isoFinancialInstitutions?.length > 2;
    const isFourStep = response?.wireInquiryResponse?.isoFinancialInstitutions?.length > 3;
    const firstIntermediaryPosition = isFourStep ? 3 : (isThreeStep ? 2 : 1);
    const { wireInquiryResponse } = response;
    // TODO: SAC-369214 remove the dynamic positioning logic
    // const isThreeStep = !!wireInquiryResponse?.isoUIFields?.secondIntermediary;
    // const isFourStep = !!wireInquiryResponse?.isoUIFields?.thirdIntermediary;
    const recipient = wireInquiryResponse?.isoUIFields?.recipient;
    // const firstIntermediary = wireInquiryResponse?.isoUIFields?.firstIntermediary;
    const secondIntermediary = wireInquiryResponse?.isoUIFields?.secondIntermediary;
    const thirdIntermediary = wireInquiryResponse?.isoUIFields?.thirdIntermediary;
    // const recipientBank = wireInquiryResponse?.isoUIFields?.recipientBank;
    const titleCasePipe = new TitleCasePipe();
    let responseForm: WireForm = {
      ...state,
      transactionType: 'wire',
      extraPrompts: {
        ...state.extraPrompts,
        useStandingAuth: wireInquiryResponse.isTransactionFromSLOAForWire ? 'yes' : 'no',
        useTemplates: 'no'
      },
      transactionDetails: {
        ...state.transactionDetails,
        selectedDate: wireInquiryResponse.processDate,
        taxWithholdingsFormGroup: {
          ...state.transactionDetails.taxWithholdingsFormGroup!
        },
        remittanceInformation: wireInquiryResponse.remitText ? wireInquiryResponse.remitText.join(' ') : '',
        transmissionNotes: wireInquiryResponse.notesText ? wireInquiryResponse.notesText.join(' ') : '',
        recipient: wireInquiryResponse.isThirdPartyTransaction ? 'thirdParty' : 'firstParty',
        trustIncomePercent: wireInquiryResponse.ptrsTransactions.length != 0 ? wireInquiryResponse.ptrsTransactions[0].incomePercent + '%' : '',
        trustPrincipalPercent: wireInquiryResponse.ptrsTransactions.length != 0 ? wireInquiryResponse.ptrsTransactions[0].principalPercent + '%' : '',
        trustTaxCode: wireInquiryResponse.ptrsTransactions.length != 0 ? wireInquiryResponse.ptrsTransactions[0].taxCode : '',
        trustTaxCategory: wireInquiryResponse.ptrsTransactions.length != 0 ? wireInquiryResponse.ptrsTransactions[0].taxCategory : '',
        trustComment: wireInquiryResponse.ptrsTransactions.length != 0 ? wireInquiryResponse.ptrsTransactions[0].comment : '',
        labelRefNum: wireInquiryResponse.labelRefNum,
        refNum: !!wireInquiryResponse.refNum ? wireInquiryResponse.refNum : 'To be determined',
        requestedDate: wireInquiryResponse.requestedDate,
        isISOCompliant: wireInquiryResponse.isISOCompliant,
        receivingBankCountry: wireInquiryResponse.receivingBankCountry
      }
    } as WireForm;
    if (wireInquiryResponse.isISOCompliant) {
      responseForm.transactionDetails = {
        ...responseForm.transactionDetails,

      // Receiving Bank/ABA bank DETAILS SECTION
      // the first section under the intermediary header determining the aba details
      aba: wireInquiryResponse?.isoFinancialInstitutions[0]?.identifierList[0]?.identifierValue,
      bankID: wireInquiryResponse?.isoFinancialInstitutions[0]?.identifierList[0]?.identifierValue,
      receivingBankName:
        wireInquiryResponse?.isoFinancialInstitutions[0]?.name,
      receivingBankCity: wireInquiryResponse?.isoFinancialInstitutions[0]?.postalAddress?.townName?.trim(),
      receivingBankState: wireInquiryResponse?.isoFinancialInstitutions[0]?.postalAddress?.countrySubDivision?.trim(),
      // mapping to be changed as part of future story after cleanup at backend SAC-369214
      receivingBankCountry: wireInquiryResponse?.isoFinancialInstitutions?.length > 1 ? wireInquiryResponse?.intermediaryCountry : wireInquiryResponse?.receivingBankCountry,
      bankPhoneNumber: wireInquiryResponse?.receivingBankPhone,
      canDisplayReceivingBankPhone: wireInquiryResponse?.canDisplayReceivingBankPhone,

      // Recipient section below which shows first/third party details
      toAccount: wireInquiryResponse.beneficiaryAccountNumber,
      name: wireInquiryResponse.beneficiaryName[0],
      receivingAccountHolderName: wireInquiryResponse.beneficiaryName[0],
      receivingAccountHolderStreetName: recipient?.postalAddress?.streetName ?? '',
      receivingAccountHolderStreetNumber: recipient?.postalAddress?.buildingNumber ?? '',
      receivingAccountHolderApartmentSuiteEtc: recipient?.postalAddress?.room ?? '',
      receivingAccountHolderCity: recipient?.postalAddress?.townName ?? '',
      receivingAccountHolderState: recipient?.postalAddress?.countrySubDivision ?? '',
      receivingAccountHolderZip: recipient?.postalAddress?.postCode ?? '',
      receivingAccountHolderDepartment: recipient?.postalAddress?.department ?? '',
      receivingAccountHolderBuildingName: recipient?.postalAddress?.buildingName ?? '',
      receivingAccountHolderFloor: recipient?.postalAddress?.floor ?? '',
      receivingAccountHolderTownLocationName: recipient?.postalAddress?.townLocationName ?? '',
      receivingAccountHolderDistrict: recipient?.postalAddress?.districtName ?? '',
      receivingAccountHolderCountry: wireInquiryResponse.beneficiaryCountry,
      receivingBankName1: wireInquiryResponse.receivingBankName[0] ?? '',
      receivingBankName2: wireInquiryResponse.receivingBankName[1] ?? '',
      receivingBankIdentifierList: wireInquiryResponse.isoFinancialInstitutions[0]?.identifierList,

      // first intermediary bank details which displays under recipient bank section on all pages
      intermediarySelection: wireInquiryResponse.intermediaryName ? yesNo.yes : yesNo.no,
      intermediaryIdentifierList:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList,
      intermediaryName:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.name,
      intermediaryAccountNumber:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "Account")[0]?.identifierValue,
      intermediaryIBAN:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "IBAN")[0]?.identifierValue,
      intermediaryBIC:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue,
      intermediaryClearingSysId:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType != "Account" && x.identifierType != "IBAN" && x.identifierType != "BIC")[0]?.identifierValue,
      intermediaryStreetNumber: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.buildingNumber?.trim(),
      intermediaryStreetName: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.streetName?.trim(),
      intermediaryApartmentSuiteEtc: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.room?.trim(),
      intermediaryCity: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.townName?.trim(),
      intermediaryState: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.countrySubDivision?.trim(),
      intermediaryZip: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.postCode?.trim(),
      intermediaryDepartment: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.department?.trim(),
      intermediaryBuildingName: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.buildingName?.trim(),
      intermediaryFloor: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.floor?.trim(),
      intermediaryTownLocationName: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.townLocationName?.trim(),
      intermediaryDistrict: wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.districtName?.trim(),
      // mapping to be changed as part of future story after cleanup at backend SAC-369214
      intermediaryCountry:
        wireInquiryResponse?.receivingBankCountry,
      intermediaryPhone:
        wireInquiryResponse?.isoFinancialInstitutions[firstIntermediaryPosition]?.phone ?? '',
      canDisplayIntermediaryPhone: wireInquiryResponse.canDisplayIntermediaryPhone,
      // second Intermediary Section
      creditToSelection: wireInquiryResponse?.isoFinancialInstitutions?.length > 2
        ? 'yes'
        : 'no',
      isThreeStep: isThreeStep,
      secondIntermediaryIdentifierList: secondIntermediary?.identifierList ?? [],
      secondIntermediaryName: secondIntermediary?.name?.trim() ?? '',
      secondIntermediaryAccountNumber: secondIntermediary?.accountNumber?.identifierValue ?? '',
      secondIntermediaryIBAN: secondIntermediary?.iban?.identifierValue ?? '',
      secondIntermediaryBIC: secondIntermediary?.bic?.identifierValue ?? '',
      secondIntermediaryClearingSysId: secondIntermediary?.clearingSystemBankId?.identifierValue ?? '',
      secondIntermediaryStreetNumber: secondIntermediary?.postalAddress?.buildingNumber?.trim() ?? '',
      secondIntermediaryStreetName: secondIntermediary?.postalAddress?.streetName?.trim() ?? '',
      secondIntermediaryApartmentSuiteEtc: secondIntermediary?.postalAddress?.room?.trim() ?? '',
      secondIntermediaryCity: secondIntermediary?.postalAddress?.townName?.trim() ?? '',
      secondIntermediaryState: secondIntermediary?.postalAddress?.countrySubDivision?.trim() ?? '',
      secondIntermediaryZip: secondIntermediary?.postalAddress?.postCode?.trim() ?? '',
      secondIntermediaryDepartment: secondIntermediary?.postalAddress?.department?.trim() ?? '',
      secondIntermediaryBuildingName: secondIntermediary?.postalAddress?.buildingName?.trim() ?? '',
      secondIntermediaryFloor: secondIntermediary?.postalAddress?.floor?.trim() ?? '',
      secondIntermediaryTownLocationName: secondIntermediary?.postalAddress?.townLocationName?.trim() ?? '',
      secondIntermediaryDistrict: secondIntermediary?.postalAddress?.districtName?.trim() ?? '',
      // mapping to be changed as part of future story after cleanup at backend SAC-369214
      secondIntermediaryCountry: wireInquiryResponse?.creditToCountry ?? '',
      creditToPhone: wireInquiryResponse?.creditToPhone ?? '',
      canDisplayCreditToPhone: wireInquiryResponse.canDisplayCreditToPhone,

      // Third Intermediary Section
      isFourStep: isFourStep,
      thirdIntermediaryIdentifierList: thirdIntermediary?.identifierList ?? [],
      thirdIntermediaryName: thirdIntermediary?.name?.trim() ?? '',
      thirdIntermediaryAccountNumber: thirdIntermediary?.accountNumber?.identifierValue ?? '',
      thirdIntermediaryIBAN: thirdIntermediary?.iban?.identifierValue ?? '',
      thirdIntermediaryBIC: thirdIntermediary?.bic?.identifierValue ?? '',
      thirdIntermediaryClearingSysId: thirdIntermediary?.clearingSystemBankId?.identifierValue ?? '',
      thirdIntermediaryStreetNumber: thirdIntermediary?.postalAddress?.buildingNumber?.trim() ?? '',
      thirdIntermediaryStreetName: thirdIntermediary?.postalAddress?.streetName?.trim() ?? '',
      thirdIntermediaryApartmentSuiteEtc: thirdIntermediary?.postalAddress?.room?.trim() ?? '',
      thirdIntermediaryCity: thirdIntermediary?.postalAddress?.townName?.trim() ?? '',
      thirdIntermediaryState: thirdIntermediary?.postalAddress?.countrySubDivision?.trim() ?? '',
      thirdIntermediaryZip: thirdIntermediary?.postalAddress?.postCode?.trim() ?? '',
      thirdIntermediaryDepartment: thirdIntermediary?.postalAddress?.department?.trim() ?? '',
      thirdIntermediaryBuildingName: thirdIntermediary?.postalAddress?.buildingName?.trim() ?? '',
      thirdIntermediaryFloor: thirdIntermediary?.postalAddress?.floor?.trim() ?? '',
      thirdIntermediaryTownLocationName: thirdIntermediary?.postalAddress?.townLocationName?.trim() ?? '',
      thirdIntermediaryDistrict: thirdIntermediary?.postalAddress?.districtName?.trim() ?? '',
      // mapping to be changed as part of future story after cleanup at backend SAC-369214
      thirdIntermediaryCountry: wireInquiryResponse?.thirdIntermediaryCountry,

      // Recipient Account, Name and Address
      recipient:
      wireInquiryResponse?.isThirdPartyTransaction
          ? 'thirdParty'
          : 'firstParty',
      // Use SLOA flow wont have the checkbox checked even though the SLOA created has Escrow
      isEscrow: false,
      isISOCompliant: wireInquiryResponse?.isISOCompliant,
      remittanceInformation: wireInquiryResponse?.remitText?.length > 0 ? wireInquiryResponse?.remitText[0] : '',
      transmissionNotes: wireInquiryResponse?.notesText[0],
      }
    }
    else {
      responseForm.transactionDetails = {
        ...responseForm.transactionDetails,
        aba: wireInquiryResponse.receivingBankAba,
        bankID: wireInquiryResponse.receivingBankAba,
        receivingBankName1: wireInquiryResponse.receivingBankName[0],
        receivingBankName2: wireInquiryResponse.receivingBankName[1],
        receivingBankAddress1: wireInquiryResponse.receivingBankAddress[0],
        receivingBankAddress2: wireInquiryResponse.receivingBankAddress[1],
        receivingBankCountry: wireInquiryResponse.receivingBankCountry,
        bankPhoneNumber: wireInquiryResponse.receivingBankPhone,
        canDisplayReceivingBankPhone: wireInquiryResponse.canDisplayReceivingBankPhone,
        toAccount: wireInquiryResponse.beneficiaryAccountNumber,
        receivingAccountHolderName1: wireInquiryResponse.beneficiaryName[0],
        receivingAccountHolderName2: wireInquiryResponse.beneficiaryName[1],
        receivingAccountHolderAddress1: wireInquiryResponse.beneficiaryAddress[0],
        receivingAccountHolderAddress2: wireInquiryResponse.beneficiaryAddress[1],
        receivingAccountHolderCountry: wireInquiryResponse.beneficiaryCountry,
        intermediarySelection: wireInquiryResponse.intermediaryName ? yesNo.yes : yesNo.no,
        intermediaryName: wireInquiryResponse.intermediaryName ? wireInquiryResponse.intermediaryName[0] : '',
        intermediaryNameCont: wireInquiryResponse.intermediaryName ? wireInquiryResponse.intermediaryName[1] : '',
        intermediaryAccountNumber: wireInquiryResponse.intermediaryAccountNumber,
        intermediaryAddress: wireInquiryResponse.intermediaryAddress ? wireInquiryResponse.intermediaryAddress[0] : '',
        intermediaryAddressCont: wireInquiryResponse.intermediaryAddress ? wireInquiryResponse.intermediaryAddress[1] : '',
        intermediaryPhone: wireInquiryResponse.intermediaryPhone,
        canDisplayIntermediaryPhone: wireInquiryResponse.canDisplayIntermediaryPhone,
        intermediaryCountry: wireInquiryResponse.intermediaryCountry,
        creditToSelection: wireInquiryResponse.creditToName ? yesNo.yes : yesNo.no,
        creditToAccountNumber: wireInquiryResponse.creditToAccountNumber,
        creditToName: wireInquiryResponse.creditToName ? wireInquiryResponse.creditToName[0] : '',
        creditToNameCont: wireInquiryResponse.creditToName && wireInquiryResponse.creditToName.length > 1 ? wireInquiryResponse.creditToName[1] : '',
        creditToAddress: wireInquiryResponse.creditToAddress ? wireInquiryResponse.creditToAddress[0] : '',
        creditToAddressCont: wireInquiryResponse.creditToAddress && wireInquiryResponse.creditToAddress.length > 1 ? wireInquiryResponse.creditToAddress[1] : '',
        creditToCountry: wireInquiryResponse.creditToCountry,
        creditToPhone: wireInquiryResponse.creditToPhone,
        canDisplayCreditToPhone: wireInquiryResponse.canDisplayCreditToPhone,
      }
    }

    if (wireInquiryResponse.bSLOATxn){
      responseForm.extraPrompts = {
        ...responseForm.extraPrompts,
        useStandingAuth: 'bundled',
        useBundledStandingAuth: wireInquiryResponse.isTransactionFromSLOAForWire ? 'yes' : 'no'
      }
    }

    return responseForm;
  }),
  on(AchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
    const percentFormatPipe = new FormatPercentPipe();
    let achWithdrawalDate = achInquiryResponse.frequency === 'On-Request' ? achInquiryResponse.processDate : achInquiryResponse.nextProcessDate;
    return {
      ...state,
      transactionType: transactionTypes.ach,
      extraPrompts: {
        ...state.extraPrompts,
        transferDirection: achInquiryResponse.incoming ? 'incoming' : 'outgoing'
      },
      transactionDetails: {
        ...state.transactionDetails,
        /*
          TODO: Withdrawal date received from Inquiry call is sometimes incorrect (doesn't take into account federal holidays)
        */
        withdrawalDate: achWithdrawalDate,
        completionDate: achInquiryResponse.processDate,
        fundsAvailableToTrade: achWithdrawalDate,
        amount: achInquiryResponse.grossNetIndicator === 'N' ? achInquiryResponse.netAmt : achInquiryResponse.grossAmt,
        amountType: achInquiryResponse.grossNetIndicator === 'N' ? amountTypeDict.net : amountTypeDict.gross,
        contributionReason: achInquiryResponse.contributionReason,
        trustIncomePercent: achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(achInquiryResponse.trustFields.trustIncmPct) : '',
        trustPrincipalPercent: achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(achInquiryResponse.trustFields.trustPrinPct) : '',
        trustTaxCode: achInquiryResponse.trustFields.isTrust ? achInquiryResponse.trustFields.trustTaxCode : '',
        trustPaidFor: achInquiryResponse.trustFields.paidFor,
        trustComment: achInquiryResponse.trustFields.tranMemo,
        taxWithholdingsFormGroup: {
          ...state.transactionDetails.taxWithholdingsFormGroup,
          distributionReason: achInquiryResponse.iraFields.isIraTransaction ? achInquiryResponse.distributionReasonDescription : '',
          fedTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.fedTaxPercentage) : '',
          stateTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.stateTaxPercentage, 3) : '',
        }
      }
    } as AchForm;
  }),
  on(AchActions.verifyCallSuccessful, (state, {response, initiationDate}) => {
    const validEndDate = response.validProfileEndDate && !response.validProfileEndDate.startsWith('9999') && !response.validProfileEndDate.startsWith('0001')
      ? getDateWOTimezone(response.validProfileEndDate)
      : undefined;
    return {
      ...state,
      transactionDetails: {
        ...state.transactionDetails,
        withdrawalDate: getDateWOTimezone(initiationDate),
        completionDate: response.processDate,
        contributionReason: response.contributionDescription?.trim() || null,
        endDate: validEndDate ?? (state.transactionDetails as AchTransactionDetails).endDate
      }
    }
  }),
  on(CheckActions.incomingInquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    return {
      ...state,
      transactionType: transactionTypes.check,
      transactionDetails: {
        ...state.transactionDetails,
        processDate: checkInquiryResponse.processDate
      }
    }
  }),
  on(CheckActions.callInquiry, InternalTransferActions.callInquiry, (state) => {
    return {
      ...state,
      loading: true
    }
  }),
  on(CheckActions.inquiryCallFailed, InternalTransferActions.inquiryCallFail, (state) => {
    return {
      ...state,
      loading: false
    }
  }),
  on(CheckActions.inquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    const { cashieringGrossAndNet } = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction ?? {};
    const isRecipientAccountHolder = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressofrecord';
    const isThirdPartyInternational = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressforeign';
    const useStandingAuth = checkInquiryResponse?.cashieringCheckInputDetails.isTransactionFromSLOAOrSLOA;
    const isThirdParty: boolean = checkInquiryResponse.isThirdParty;
    let amountTypeSelection = getGrossNetSelection(cashieringGrossAndNet.grossAmountOriginCode, cashieringGrossAndNet.netAmountOriginCode);
    let transactionForm: CheckForm = {
      ...state,
      loading: false,
      stateList: checkInquiryResponse?.stateList,
      countryList: checkInquiryResponse?.countryList,
      transactionType: transactionTypes.check,
      extraPrompts: {
        ...state.extraPrompts,
        useStandingAuth: checkInquiryResponse.isBundledSloaUsage ? 'bundled' : useStandingAuth ? yesNo.yes : yesNo.no,
        frequency: getFrequency(checkInquiryResponse.cashieringCheckInputDetails.frequencyCode)
      },
      transactionDetails: {
        ...state.transactionDetails,
        checkMemo: checkInquiryResponse.memo,
        processDate: `${checkInquiryResponse.processDateMM}/${checkInquiryResponse.processDateDD}/${checkInquiryResponse.processDateYYYY}`,
        secondProcessDate: checkInquiryResponse?.cashieringCheckInputDetails?.frequencyCode === 'SEMIMTH' && 
          checkInquiryResponse.process2ndDateMM && checkInquiryResponse.process2ndDateDD && checkInquiryResponse.process2ndDateYYYY
          ? `${checkInquiryResponse.process2ndDateMM}/${checkInquiryResponse.process2ndDateDD}/${checkInquiryResponse.process2ndDateYYYY}` 
          : '',
        endDate: ((!checkInquiryResponse?.endDateYYYY) || (checkInquiryResponse?.endDateYYYY ==='9999')) ? '' : 
          (checkInquiryResponse.endDateMM && checkInquiryResponse.endDateDD && checkInquiryResponse.endDateYYYY) 
          ? `${checkInquiryResponse.endDateMM}/${checkInquiryResponse.endDateDD}/${checkInquiryResponse.endDateYYYY}` 
          : '',
        amount: amountFormatter.format(amountTypeSelection === 'net' ?  cashieringGrossAndNet.requestNetAmount : cashieringGrossAndNet.requestGrossAmount),
        amountType: getGrossNetSelection(cashieringGrossAndNet?.grossAmountOriginCode, cashieringGrossAndNet?.netAmountOriginCode),
        payeeType: isThirdParty ? 'thirdParty' : 'firstParty',
        distributionReasonCode: checkInquiryResponse.distributionReasonCode,
        checkReason: checkInquiryResponse?.withdrawalReasonCode,
        memoLine2: checkInquiryResponse?.memoLine2,
        deliveryMethod: CheckDeliveryCodesType[checkInquiryResponse.deliveryMethodCode],
        recipient: checkRecipientTypeDict[checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType as keyof typeof checkRecipientTypeDict] as CheckAbbreviatedRecipientTypes,
        taxWithholdingsFormGroup: {
          ...state.transactionDetails.taxWithholdingsFormGroup!,
          fedTaxPercent: checkInquiryResponse?.iraaccount ? getFormattedTaxPercent(checkInquiryResponse?.fedTaxAmountFormatted)! : '',
          stateTaxPercent: checkInquiryResponse?.iraaccount ? getFormattedTaxPercent(checkInquiryResponse?.stateTaxAmountFormatted)! : '',
          distributionReasonMap: [{
            code: checkInquiryResponse.distributionReasonCode,
            subCode: checkInquiryResponse.distributionReasonCodeSubcode,
            description: checkInquiryResponse.distributionReasonDescription,
            isTaxExempt: false
          }],
          distributionReason: checkInquiryResponse.distributionReasonDescription,
          fedTaxAmount: checkInquiryResponse?.fedTaxAmountFormatted,
          stateTaxAmount: checkInquiryResponse?.stateTaxAmountFormatted,
          stateHasTaxes: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.withholdStateTaxIndicator
        },
        recurringFrequency: checkInquiryResponse?.cashieringCheckInputDetails?.frequencyCode,
        frequencyDescription: checkInquiryResponse.frequencyDesc,
        payIncome: checkInquiryResponse?.cashieringCheckInputDetails?.payIncomeCode?.trim(),
        addresseeName: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[0]?.mailName ?? '',
        addresseeNameCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[1]?.mailName ?? '',
        addresseeNameCont2: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[2]?.mailName ?? '',
        streetAddress: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine1 ?? '',
        streetAddressCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine2 ?? '',
        city: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.city ?? '',
        zipCode: (isRecipientAccountHolder || isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
          ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
          : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
        state: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateCode,
        country: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.countryCode,
        stateProvince: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateName,
        postalCode: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
          ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
          : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
        defaultPayee: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee,
        nextProcessDate: checkInquiryResponse?.cashieringCheckInputDetails?.nextProcessDate,
        trustTaxCategory: getTrustTaxCategory(checkInquiryResponse?.taxCodeList, checkInquiryResponse?.trustTaxCode)
      }
    } as CheckForm;

    if (checkInquiryResponse.isBundledSloaUsage){
      transactionForm.extraPrompts.useBundledStandingAuth = useStandingAuth ? yesNo.yes : checkInquiryResponse.cashieringCheckInputDetails.isRecurringTransaction ? 'recurring' : yesNo.no;
    }

    // Set third party fields
    if (isThirdParty) {
      transactionForm.transactionDetails.payeeName = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails?.payeeName1;
      if (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails?.payeeName2?.trim()) {
        transactionForm.transactionDetails.additionalPayeeName1 = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails?.payeeName2;
      }
      if (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails?.payeeName3?.trim()) {
        transactionForm.transactionDetails.additionalPayeeName2 = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails?.payeeName3;
      }
    }
    else {
      if (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee[0]?.length > 0) {
        transactionForm.transactionDetails.payeeName = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee[0];
      }
    }

    if (checkInquiryResponse.ptrsTransactions.length > 0) {
      let transInquiryTrustFields = mapApiResponseToTransInquiryInfo(checkInquiryResponse.ptrsTransactions);
      const titleCasePipe = new TitleCasePipe();
      transactionForm = {
        ...transactionForm,
        transactionDetails: {
          ...transactionForm.transactionDetails,
          trustTaxCategory: getTrustTaxCategory(checkInquiryResponse?.taxCodeList, transInquiryTrustFields.trustTaxCode),
          trustIncomePercent: transInquiryTrustFields.trustIncomePercent + '%',
          trustPrincipalPercent: transInquiryTrustFields.trustPrincipalPercent + '%',
          trustTaxCode: transInquiryTrustFields.trustTaxCode,
          trustPaidFor: titleCasePipe.transform(transInquiryTrustFields.trustPaidFor),
          trustComment: transInquiryTrustFields.trustComment
        }
      }
    }

    return transactionForm;
  }),
  on(PtrsActions.transInquiryCallSuccessful, (state, response) => {
    const { transInquiryResponse } = response;
    const titleCasePipe = new TitleCasePipe();
    return {
      ...state,
      transactionDetails: {
        ...state.transactionDetails,
        trustIncomePercent: !transInquiryResponse?.trustIncomePercent ? '' : transInquiryResponse.trustIncomePercent + '%',
        trustPrincipalPercent: !transInquiryResponse?.trustPrincipalPercent ? '' : transInquiryResponse.trustPrincipalPercent + '%',
        trustTaxCode: transInquiryResponse.trustTaxCode,
        trustPaidFor: titleCasePipe.transform(transInquiryResponse.trustPaidFor),
        trustIncomeToPercent: !transInquiryResponse?.trustIncomeToPercent ? '' : transInquiryResponse.trustIncomeToPercent + '%',
        trustPrincipalToPercent: !transInquiryResponse?.trustPrincipalToPercent ? '' : transInquiryResponse.trustPrincipalToPercent + '%',
        trustDepositCode: transInquiryResponse.trustDepositCode,
        trustComment: transInquiryResponse.trustComment
      }
    }
  }),
  on(SharedActions.updateSelectedDate, (state, response) => {
    return {
      ...state,
      transactionDetails: {
        ...state.transactionDetails,
        selectedDate: response.newDate
      }
    }
  }),
  on(TransactionsTableActions.setCurrentViewedTemplate, (state, payload) => {
    if (payload.template?.transactionType === 'wire') {
      return {
        ...state,
        transactionType: 'wire',
        transactionDetails: {
          ...state.transactionDetails,
          aba: payload.template?.draftObject.beneficiaryBankId ?? '',
          receivingBankName1: payload.template?.draftObject.beneficiaryBank.namePartOne ?? '',
          receivingBankName2: payload.template?.draftObject.beneficiaryBank.namePartTwo ?? '',
          receivingBankAddress1: payload.template?.draftObject.beneficiaryBank.addressLineOne ?? '',
          receivingBankAddress2: payload.template?.draftObject.beneficiaryBank.addressLineTwo ?? '',
          receivingBankCountry: payload.template?.draftObject.beneficiaryBank.countryName ?? '',
          intermediarySelection: payload.template?.draftObject.intermediary.id === '' ? yesNo.no : yesNo.yes,
          intermediaryName: payload.template?.draftObject?.intermediary?.mailingDetails?.name ?
            payload.template?.draftObject?.intermediary?.mailingDetails?.name :
            payload.template?.draftObject.intermediary.mailingDetails.namePartOne ?? '',
          intermediaryNameCont: payload.template?.draftObject?.intermediary?.mailingDetails?.name ? '' :
            payload.template?.draftObject.intermediary.mailingDetails.namePartTwo ?? '',
          intermediaryAccountNumber: payload.template?.draftObject.intermediary.id ?? '',
          intermediaryAddress: payload.template?.draftObject.intermediary.mailingDetails.addressLineOne ?? '',
          intermediaryAddressCont: payload.template?.draftObject.intermediary.mailingDetails.addressLineTwo ?? '',
          intermediaryCountry: payload.template?.draftObject.intermediary.mailingDetails.countryName ?? '',
          intermediaryPhone: payload.template?.draftObject.intermediary.mailingDetails.phoneNumber ?? '',
          canDisplayIntermediaryPhone: payload.template?.draftObject.intermediary.mailingDetails.phoneNumber !== '',
          toAccount: payload.template?.draftObject.beneficiaryAccount ?? '',
          receivingAccountHolderName1: payload.template?.draftObject.beneficiary.namePartOne ?? '',
          receivingAccountHolderName2: payload.template?.draftObject.beneficiary.namePartTwo ?? '',
          receivingAccountHolderAddress1: payload.template?.draftObject.beneficiary.addressLineOne ?? '',
          receivingAccountHolderAddress2: payload.template?.draftObject.beneficiary.addressLineTwo  ?? '',
          receivingAccountHolderCountry: payload.template?.draftObject.beneficiary.countryName ?? '',
          recipient: payload.template?.draftObject.isThirdParty ? 'thirdParty' : 'firstParty',
          transmissionNotes: payload.template?.draftObject.note,
          //additional ISO mapping
          isISOCompliant: payload.template?.draftObject?.isIsoCompliant,
          name: payload.template?.draftObject?.beneficiary?.name,
          receivingAccountHolderStreetNumber: payload.template?.draftObject?.beneficiary?.buildingNumber,
          receivingAccountHolderStreetName: payload.template?.draftObject?.beneficiary?.streetName,
          receivingAccountHolderCity: payload.template?.draftObject?.beneficiary?.townName,
          receivingAccountHolderState: payload.template?.draftObject?.beneficiary?.countrySubDivision,
          receivingAccountHolderZip: payload.template?.draftObject?.beneficiary?.zipCode,
          receivingAccountHolderApartmentSuiteEtc: payload.template?.draftObject?.beneficiary?.room,
          receivingAccountHolderDepartment: payload.template?.draftObject?.beneficiary?.department,
          receivingAccountHolderBuildingName: payload.template?.draftObject?.beneficiary?.buildingName,
          receivingAccountHolderFloor: payload.template?.draftObject?.beneficiary?.floor,
          receivingAccountHolderDistrict: payload.template?.draftObject?.beneficiary?.districtName ?? '',
          receivingBankName: payload.template?.draftObject?.beneficiaryBank?.name,
          receivingBankCity: payload.template?.draftObject?.beneficiaryBank?.townName,
          receivingBankState: payload.template?.draftObject?.beneficiaryBank?.countrySubDivision,
          intermediaryStreetNumber: payload.template?.draftObject?.intermediary?.mailingDetails?.buildingNumber,
          intermediaryStreetName: payload.template?.draftObject?.intermediary?.mailingDetails?.streetName,
          intermediaryApartmentSuiteEtc: payload.template?.draftObject?.intermediary?.mailingDetails?.room,
          intermediaryDepartment: payload.template?.draftObject?.intermediary?.mailingDetails?.department,
          intermediaryBuildingName: payload.template?.draftObject?.intermediary?.mailingDetails?.buildingName,
          intermediaryFloor: payload.template?.draftObject?.intermediary?.mailingDetails?.floor,
          intermediaryCity: payload.template?.draftObject?.intermediary?.mailingDetails?.townName,
          intermediaryState: payload.template?.draftObject?.intermediary?.mailingDetails?.countrySubDivision,
          intermediaryZip: payload.template?.draftObject?.intermediary?.mailingDetails?.zipCode
          //end
        }
      } as WireForm
    }
    else {
      return {
        ...state
      }
    }
  }),
  on(TransactionsTableActions.startTransactionClicked, ViewPageActions.startTransactionButtonClicked,  (state, payload) => {
    return {
      ...state,
      transactionType: transactionTypes.wire,
      extraPrompts: {
        useStandingAuth: 'no',
        useTemplates: 'yes',
        templateId: payload.draftId
      } as WireExtraPrompts
    }
  })
);

function getTrustTaxCategory(categoriesAndCodes: TrustTaxCategoriesAndCodes, taxCode: string): string {
  if (taxCode && categoriesAndCodes) {
    var category = Object.entries(categoriesAndCodes).find(category => category[1].includes(taxCode ?? ''));
    if (category) {
      return category[0];
    }
  }
  return '';
}

function getFrequency(frequencyCode: string): string {
  if (frequencyCode === 'ONETIME' || frequencyCode === 'ONREQST') {
    return Frequencies.onetime;
  }
  return Frequencies.recurring;
}

function getFormattedTaxPercent(original: string): string {
  let newVal = original.split(' ')[1];
  if (newVal) {
    return newVal?.replace('(', '').replace(')', '');
  } else {
    return original;
  }
}

/**
 *
 * @param grossOriginCode Either a 'D' or an 'E' (Entered/Calcualted on REST) -> 'D' means that the gross amount was calculated by ATS, 'E' means it was entered by the user
 * @param netOriginCode Either a 'D' or an 'E' (Entered/Calcualted on REST) -> 'D' means that the net amount was calculated by ATS, 'E' means it was entered by the user
 * @returns Either 'gross' or 'net'. If the gross code is 'E' then that means the user entered the amount as gross. If the gross code is 'D' that means the user entered the amount as net
 * @returns 'gross' if netOriginCode is ' '.
 */
export function getGrossNetSelection(grossOriginCode: string, netOriginCode: string): 'gross' | 'net' {
  return grossOriginCode === 'E' || netOriginCode === 'D' || netOriginCode === ' ' ? 'gross' : 'net';
}
